import type { PostEntryInput, PostingLineInput } from '@taler/api-client'
import { addMoney, type CurrencyCode, type LocalDate, type Money, zeroMoney } from '@taler/domain'
import type { Target } from './targets.ts'

/**
 * The fallback for an entry with no assumed kind: no primary/counter roles, just a raw
 * list of postings the user signs themselves. This is what `PostEntryRequest.postings`
 * actually is - the flow grid is a keyboard-friendly assumption layered on top of it. A
 * line's amount is signed (the user picks the sign directly, there being no kind to derive
 * it from), enforced by the `AmountInput` it's typed into.
 */
export interface GenericLineDraft {
  readonly id: string
  targetId: string | undefined
  amount: Money | null
  memo: string
}

export interface GenericEntryDraft {
  readonly id: string
  date: LocalDate
  memo: string
  tags: string[]
  lines: GenericLineDraft[]
}

export function createGenericLine(): GenericLineDraft {
  return { id: crypto.randomUUID(), targetId: undefined, amount: null, memo: '' }
}

export function createGenericEntry(date: LocalDate): GenericEntryDraft {
  return {
    id: crypto.randomUUID(),
    date,
    memo: '',
    tags: [],
    lines: [createGenericLine(), createGenericLine()],
  }
}

/** The currency every complete line has committed to - undefined until at least one
 * line names a fixed-currency target, since a category-only entry has none yet. */
function resolveCurrency(
  entry: GenericEntryDraft,
  targetsById: ReadonlyMap<string, Target>,
): CurrencyCode | undefined {
  for (const line of entry.lines) {
    const currency = line.targetId === undefined ? undefined : targetsById.get(line.targetId)?.currency
    if (currency !== undefined) return currency
  }
  return undefined
}

export interface GenericValidation {
  readonly currency: CurrencyCode | undefined
  readonly balance: Money | undefined
  readonly isBalanced: boolean
  readonly isComplete: boolean
}

export function validateGenericEntry(
  entry: GenericEntryDraft,
  targetsById: ReadonlyMap<string, Target>,
): GenericValidation {
  const currency = resolveCurrency(entry, targetsById)
  const complete =
    entry.lines.length >= 2 &&
    entry.lines.every((line) => line.targetId !== undefined && line.amount !== null)
  if (currency === undefined || !complete) {
    return { currency, balance: undefined, isBalanced: false, isComplete: false }
  }
  for (const line of entry.lines) {
    const target = targetsById.get(line.targetId!)
    if (target?.currency !== undefined && target.currency !== currency) {
      return { currency, balance: undefined, isBalanced: false, isComplete: false }
    }
  }
  const balance = entry.lines.reduce((sum, line) => addMoney(sum, line.amount!), zeroMoney(currency))
  return { currency, balance, isBalanced: balance.units === 0n, isComplete: true }
}

export function toPostEntryInput(
  entry: GenericEntryDraft,
  targetsById: ReadonlyMap<string, Target>,
): PostEntryInput | undefined {
  const validation = validateGenericEntry(entry, targetsById)
  if (!validation.isComplete || !validation.isBalanced || validation.currency === undefined) return undefined
  const postings: PostingLineInput[] = entry.lines.map((line) => ({
    accountId: line.targetId!,
    amount: line.amount!,
    ...(line.memo.trim() === '' ? {} : { memo: line.memo.trim() }),
  }))
  return {
    date: entry.date,
    postings,
    ...(entry.memo.trim() === '' ? {} : { memo: entry.memo.trim() }),
    ...(entry.tags.length === 0 ? {} : { tags: entry.tags }),
  }
}
